import { getAuth } from "firebase/auth";
import { message } from "antd";
import {
  type ActionFunctionArgs,
  LoaderFunctionArgs,
  redirect,
} from "react-router-dom";
import { apiClient } from "~/services/apiClient";
import type { ShadowInvitePreviewData } from "~/pages/ShadowInvitePage/types";
import i18n from "~/i18n";
import { ensureTenantExists } from "~/services/server/user-tenant";
import { INTENTS, ROUTES } from "~/utils";
import {
  writeShadowInviteLinkToStorage,
  type ShadowInviteLinkPayload,
} from "~/utils/shadowInviteStorage";

export const ShadowInvitePageLoader = async ({
  request,
}: LoaderFunctionArgs) => {
  const url = new URL(request.url);
  const shadowTenantId = url.searchParams.get("shadowTenantId")?.trim() ?? "";
  const leaseId = url.searchParams.get("leaseId")?.trim() ?? "";

  if (!shadowTenantId || !leaseId) {
    return redirect("/");
  }

  const link: ShadowInviteLinkPayload = { shadowTenantId, leaseId };
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    writeShadowInviteLinkToStorage(link);
    const returnUrl = `${url.pathname}${url.search}`;
    return redirect(
      `${ROUTES.signup}?returnUrl=${encodeURIComponent(returnUrl)}`
    );
  }

  const result = await apiClient.loader<ShadowInvitePreviewData>(
    "shadow-invite-link",
    { shadowTenantId, leaseId }
  );

  if (!result.success || !result.data) {
    return {
      error: result.error ?? "Could not load invite",
      link,
    };
  }

  return { preview: result.data, link };
};

export type ShadowInvitePageLoaderType =
  | { preview: ShadowInvitePreviewData; link: ShadowInviteLinkPayload }
  | { error: string; link: ShadowInviteLinkPayload };

export const ShadowInvitePageAction = async ({
  request,
}: ActionFunctionArgs) => {
  const formData = await request.formData();
  const intent = formData.get("intent");
  if (intent !== INTENTS._shadow_invite_claim) {
    return {};
  }

  const shadowTenantId = String(formData.get("shadowTenantId") ?? "").trim();
  const leaseId = String(formData.get("leaseId") ?? "").trim();
  const returnUrl = String(formData.get("returnUrl") ?? "").trim();

  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    if (shadowTenantId && leaseId) {
      writeShadowInviteLinkToStorage({ shadowTenantId, leaseId });
    }
    const safeReturn =
      returnUrl ||
      (shadowTenantId && leaseId
        ? `${ROUTES.shadowInvite}?shadowTenantId=${encodeURIComponent(shadowTenantId)}&leaseId=${encodeURIComponent(leaseId)}`
        : ROUTES.home);
    return redirect(
      `${ROUTES.signup}?returnUrl=${encodeURIComponent(safeReturn)}`
    );
  }

  try {
    const idToken = await user.getIdToken();
    const [firstName, ...rest] = (user.displayName || "").split(" ");
    const lastName = rest.join(" ");
    writeShadowInviteLinkToStorage({ shadowTenantId, leaseId });

    const ensureResult = await ensureTenantExists(
      {
        id: user.uid,
        email: user.email || "",
        username: user.email || "",
        firstName: firstName || "",
        lastName: lastName || "",
      },
      { idToken }
    );

    if (!ensureResult.success) {
      message.error(
        typeof ensureResult.error === "string"
          ? ensureResult.error
          : i18n.t("Sorry_something_went_wrong")
      );
      return {};
    }

    return redirect(ROUTES.home);
  } catch (e) {
    console.error("[ShadowInvitePageAction]", e);
    message.error(i18n.t("Sorry_something_went_wrong"));
    return {};
  }
};
